import { Box } from "@mui/material";
import React, { useEffect } from "react";
import { TextInputComponent } from "../../../../../common/components";
import { Form, Formik, useFormikContext } from "formik";
import "./crm-plan-details.scss";
import icons from "../../../../../assets/icons/icons";
import SearchableAutoSelect from "../../../../../common/components/auto-select-search-component/search-select-component";
import { planTypeDropdown } from "../../crm-plans-utils";
import { updateCRMPlan } from "../../../../../api/crm-plans";

interface CRMPlanDetailsProps {
  mode?: string;
  handleCancel?: any;
  activeTab?: any;
  setPlans?: any;
  existingPlans?: any;
  handleSubmit?: (
    sectionType: "basicDetails" | "pricing" | "benefits",
    payload: any,
    updatePlansData: any
  ) => void;
  title?: string;
}

const CRMPlanDetails: React.FC<CRMPlanDetailsProps> = ({
  mode,
  handleCancel,
  activeTab,
  setPlans,
  handleSubmit,
  title,
  existingPlans,
}) => {
  const [trialPlanOptions, setTrialPlanOptions] =
    React.useState<any>(planTypeDropdown);
  const {
    values,
    errors,
    setFieldValue,
    handleChange,
    setFieldTouched,
    validateField,
  } = useFormikContext<any>();

  const hexColors: string[] = [
    "#D59F55",
    "#CD5C5C",
    "#4B3B31",
    "#004C4C",
    "#F87EA1",
    "#E48E66",
    "#556B2F",
    "#8E44AD",
  ];

  const onSubmit = () => {
    if (!values?.planName) {
      validateField("planName");
      setFieldTouched("planName", true);
      return;
    }
    const payload: any = {
      planName: values?.planName,
      subHeading: values?.subHeading,
      themeColor: values?.themeColor,
    };

    if (title === "Trial") {
      payload.planType = values?.planType;
      payload.planCategory = values?.planCategory.toLowerCase();
    }

    if (handleSubmit) handleSubmit("basicDetails", payload, payload);
  };

  useEffect(() => {
    if (values?.planCategory?.toLowerCase() === "trial" && mode === "create") {
      const filteredTrailPlans =
        existingPlans
          ?.filter((plan: any) => plan?.plan_category === "TRIAL")
          ?.map((plan: any) => plan?.plan_type) || [];

      const filteredOptions: any = planTypeDropdown?.map((option: any) => ({
        ...option,
        disabled: filteredTrailPlans.includes(option?.value),
      }));

      setTrialPlanOptions(filteredOptions);
    }
  }, [values?.planCategory, existingPlans, mode]);

  return (
    <Box
      className="plan-details-wrapper"
      sx={{
        borderTop: `${mode === "edit" ? `10px solid ${values?.themeColor || "white"}` : ""}`,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }} gap={1}>
        <Box className="crm-plan-input-wrapper">
          {values?.planCategory?.toLowerCase() === "paid" || values?.planCategory?.toLowerCase() === "custom" ? (
            <TextInputComponent
              fieldName="planName"
              type="text"
              onChange={handleChange}
              placeholder="Enter plan name"
              variant="standard"
              value={values?.planName || ""}
              maxlength="50"
              isAlphaNumeric
            />
          ) : (
            <p className="plan-header">
              {values?.planCategory?.toLowerCase() === "trial"
                ? "Trial Plan"
                : "No Cost Plan"}
            </p>
          )}
          <Box
            sx={{
              marginTop: `${values?.planCategory?.toLowerCase() === "paid" ? "10px" : ""}`,
            }}
          >
            <TextInputComponent
              fieldName="subHeading"
              type="text"
              onChange={handleChange}
              placeholder="Enter sub heading"
              variant="standard"
              value={values?.subHeading || ""}
              maxlength="100"
            />
          </Box>
        </Box>

        {values?.planCategory?.toLowerCase() === "trial" &&
          mode === "create" && (
            <li className="list-item time-period-select">
              <span className="item-text">Time Period</span>
              <span className="item-value">
                <SearchableAutoSelect
                  fieldName="planType"
                  optional
                  options={trialPlanOptions}
                  isShowError={!values?.planType}
                  placeholder="Time Period"
                  value={values?.planType || ""}
                  onChange={(e: any, newValue: any) => {
                    setFieldValue("planType", newValue);
                  }}
                  handleBlur={() => {
                    setFieldTouched("planType", true);
                  }}
                />
              </span>
            </li>
          )}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            gap: 1,
            overflowX: "auto",
            boxSizing: "border-box",
          }}
        >
          {hexColors?.map((color: string, index: number) => (
            <Box
              sx={{
                maxWidth: "31px",
                maxHeight: "31px",
                width: "100%",
                height: "100%",
                flex: "1 0 31px",
                minWidth: "31px",
                minHeight: "31px",
                borderRadius: "50%",
                background: color,
                position: "relative",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
              onClick={() => {
                setFieldValue("themeColor", color);
              }}
            >
              {values?.themeColor === color && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "6px",
                    left: "8px",
                  }}
                >
                  <img src={icons.Tick} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
        {mode === "edit" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              paddingBottom: "20px",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              className="cancel-button next-button-wrapper"
            >
              Save
            </button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CRMPlanDetails;
